import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { WaitlistForm } from '@/components/site/WaitlistForm';
import { WaitlistCounter } from '@/components/site/WaitlistCounter';
import { ConceptCard } from '@/components/site/ConceptCard';
import { VERTICALS_BY_CODE, verticalCopy } from '@/lib/verticals';
import { fetchShowcases } from '@/lib/api';
import { setPageMeta } from '@/lib/seo';
import { useT, useRaw, useLang } from '@/lib/i18n';
import { Sparkles, ArrowRight, Zap } from 'lucide-react';

export default function Univers() {
  const { code } = useParams();
  const t = useT();
  const raw = useRaw();
  const { lang } = useLang();
  const v = VERTICALS_BY_CODE[code];
  const vc = v ? verticalCopy(raw, code) : null;

  const [showcases, setShowcases] = useState([]);
  const [loadingShowcases, setLoadingShowcases] = useState(true);

  useEffect(() => {
    if (v && vc) {
      setPageMeta({
        title: vc.label,
        description: `${vc.promise} ${vc.intro}`,
        path: v.path,
      });
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [v, vc, lang]);

  useEffect(() => {
    if (!v) return;
    setLoadingShowcases(true);
    (async () => {
      const items = await fetchShowcases({ vertical: v.code, limit: 3, lang });
      setShowcases(items);
      setLoadingShowcases(false);
    })();
  }, [v, lang]);

  if (!v || !vc) return <Navigate to="/" replace />;

  return (
    <div data-testid={`page-univers-${v.code}`}>
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-16 md:pb-24">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="om-anim-halo absolute -top-24 left-[-8%] h-[420px] w-[420px] rounded-full blur-3xl"
              style={{ background: `radial-gradient(closest-side, ${v.glow}, transparent 70%)` }}
            />
            <div
              className="om-anim-halo absolute bottom-[-10%] right-[10%] h-[360px] w-[360px] rounded-full blur-3xl om-d-300"
              style={{ background: `radial-gradient(closest-side, ${v.glow}, transparent 70%)`, opacity: 0.6 }}
            />
          </div>

          <div className="om-container">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8">
              <div className="lg:col-span-7">
                <Link to="/" className="om-eyebrow" data-testid="univers-back">
                  {t('univers_page.back_home')}
                </Link>
                <div
                  className="mt-6 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider"
                  style={{ color: v.color, background: v.tint }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: v.color }} />
                  {t('univers_page.universe_badge_prefix')} {vc.short}
                </div>

                <h1
                  className="om-h1 mt-6 text-[42px] sm:text-[56px] lg:text-[68px] text-[var(--om-ink)]"
                  data-testid="univers-title"
                >
                  {vc.label}
                </h1>

                <p
                  className="mt-6 max-w-xl text-lg md:text-xl leading-relaxed"
                  style={{ color: v.color }}
                  data-testid="univers-promise"
                >
                  {vc.promise}
                </p>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                  {vc.intro}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to="/generateur"
                    className="om-btn-primary"
                    data-testid="univers-cta-generator"
                    style={{ background: v.color, boxShadow: `0 14px 28px -14px ${v.color}`, color: '#fff' }}
                  >
                    <Zap size={16} strokeWidth={2.4} />
                    {t('univers_page.cta_generator')}
                  </Link>
                  <a href="#waitlist-univers" className="om-btn-ghost" data-testid="univers-cta-waitlist-anchor">
                    {t('univers_page.cta_waitlist')}
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div
                  className="om-anim-fade-up relative mx-auto flex h-64 w-64 items-center justify-center rounded-[32px] border border-[var(--om-border)] om-illust-frame bg-white lg:h-80 lg:w-80"
                  style={{ boxShadow: `0 30px 60px -30px ${v.color}44` }}
                >
                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-[32px] opacity-50 blur-2xl"
                    style={{ background: `radial-gradient(closest-side, ${v.glow}, transparent 70%)` }}
                  />
                  <img
                    src={v.illustration}
                    alt={vc.label}
                    width={400}
                    height={400}
                    loading="eager"
                    fetchPriority="high"
                    data-testid="univers-illustration"
                    className="relative h-full w-full object-contain p-4"
                    draggable={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="om-container">
            <div className="max-w-2xl">
              <span className="om-eyebrow">{t('univers_page.section_use_cases_eyebrow')}</span>
              <h2 className="om-h1 mt-5 text-[32px] sm:text-[40px] lg:text-[48px] text-[var(--om-ink)]">
                {t('univers_page.section_use_cases_title')}
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {vc.use_cases.map((uc, i) => (
                <div
                  key={i}
                  data-testid={`univers-usecase-${i + 1}`}
                  className="group relative flex flex-col rounded-3xl border border-[var(--om-border)] bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-25px_rgba(10,14,26,0.2)]"
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -top-8 -right-8 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
                    style={{ background: `radial-gradient(closest-side, ${v.glow}, transparent 70%)` }}
                  />
                  <div
                    className="grid h-10 w-10 place-items-center rounded-2xl text-white"
                    style={{ background: v.color }}
                  >
                    <Sparkles size={18} strokeWidth={2.4} />
                  </div>
                  <h3 className="relative mt-5 text-lg font-bold text-[var(--om-ink)]">
                    {uc.title}
                  </h3>
                  <p className="relative mt-2 text-sm leading-relaxed text-[var(--om-muted)]">
                    {uc.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="om-container">
            <div className="max-w-2xl">
              <span className="om-eyebrow">{t('univers_page.section_showcases_eyebrow')}</span>
              <h2 className="om-h1 mt-5 text-[32px] sm:text-[40px] lg:text-[48px] text-[var(--om-ink)]">
                {t('univers_page.section_showcases_title')}
              </h2>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-[var(--om-muted)]">
                {t('univers_page.section_showcases_subtitle')}
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="univers-showcases">
              {loadingShowcases && (
                <>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-72 animate-pulse rounded-3xl border border-[var(--om-border)] bg-white/60"
                    />
                  ))}
                </>
              )}
              {!loadingShowcases && showcases.length === 0 && (
                <div className="col-span-full rounded-3xl border border-dashed border-[var(--om-border)] bg-white/50 p-10 text-center text-sm text-[var(--om-muted)]">
                  {t('univers_page.showcases_empty')}
                </div>
              )}
              {showcases.map((c, i) => (
                <ConceptCard
                  key={c.id}
                  concept={c}
                  accentColor={v.color}
                  dataTestId={`univers-showcase-${i + 1}`}
                  showPrototypeCta
                />
              ))}
            </div>

            <div className="mt-10 flex justify-center">
              <Link
                to="/generateur"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--om-border)] bg-white px-6 py-3 text-sm font-semibold text-[var(--om-ink)] transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_10px_20px_-14px_rgba(10,14,26,0.25)]"
                data-testid="univers-cta-generator-bottom"
              >
                {t('univers_page.cta_generate_mine')} <ArrowRight size={14} strokeWidth={2.4} />
              </Link>
            </div>
          </div>
        </section>

        <section id="waitlist-univers" className="pb-24 pt-10 md:pb-32">
          <div className="om-container">
            <div className="relative overflow-hidden rounded-[32px] border border-[var(--om-border)] bg-white p-10 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-14">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-24 -bottom-24 h-[380px] w-[380px] rounded-full blur-3xl"
                style={{ background: `radial-gradient(closest-side, ${v.glow}, transparent 70%)` }}
              />
              <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-7">
                  <span className="om-eyebrow">{t('univers_page.waitlist_eyebrow')}</span>
                  <h2 className="om-h1 mt-4 text-[30px] sm:text-[40px] text-[var(--om-ink)]">
                    {t('univers_page.waitlist_title_l1')}<br />
                    <span style={{ color: v.color }}>{vc.label}</span>.
                  </h2>
                </div>
                <div className="lg:col-span-5">
                  <WaitlistForm source={`univers-${v.code}`} variant="cta" />
                  <div className="mt-4">
                    <WaitlistCounter />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
